import type Page from '../../../view-models/Page'
import type { SelectQuestion } from '../../../view-models/Question'
import type ProductEnum from '../../enums/ProductEnum'
import type IPageMappings from '../../interfaces/IPageMappings'
import type IQuestionMapper from '../../interfaces/IQuestionMapper'
import type IRequestMapper from '../../interfaces/IRequestMapper'
import type IResponseMapper from '../../interfaces/IResponseMapper'
import type Account from '../../models/Account'
import type Address from '../../models/Address'
import type ProductPage from '../../models/ProductPage'
import type Risk from '../../models/Risk'
import type { IQuestionAnswer, QuestionAnswer } from '../../values/PageAnswers'
import type PageAnswers from '../../values/PageAnswers'
import type QuoteRequest from '../../values/QuoteRequest'
import type SchemeRequest from '../../values/SchemeRequest'
import type IConfiguration from '@/configurations/config-objects/IConfiguration'
import type { Dictionary } from '@/types'
import Routes from '@/constants/Routes'
import ContextLevel from '@/services/enums/ContextLevel'
import DLDCommonValuesEnum from '../../enums/DLDCommonValuesEnum'
import QuestionEnum from '../../enums/QuestionEnum'
import UnkownQuestionSetError from '../../error/UnknownQuestionSetError'
import { Marketing } from '../../models/Account'
import { CarRisk, PropertyRisk } from '../../models/Risk'
import AccountRegister from '../../values/AccountRegister'
import CarQuoteRequest, { CarMTAQuoteRequest } from '../../values/CarQuoteRequest'
import PropertyQuoteRequest, { PropertyMTAQuoteRequest } from '../../values/Property/PropertyQuoteRequest'
import BaseMapper from '../BaseMapper'
import { getCarQuoteRequest, mapCarRiskToAnswers, setupAdditionalDriverDependencies, setupAggregatorLandingDependencies, setupProposerDependencies, setupProposerVanDependencies, setupReasonsForChangesDependencies } from './CarFamilyMapperUtils'
import { getPropertyQuoteRequest, mapPropertyRiskToAnswers, setupHomeAdditionalProposerDependencies, setupHomeClaimsDependencies, setupHomeMTAClaimsDependencies, setupHomeProposerDependencies, setupPropertyDependencies, setupSpecifiedItems } from './PropertyFamilyMapperUtils'

export default class YogaMapper extends BaseMapper implements IQuestionMapper, IRequestMapper, IResponseMapper, IPageMappings {
  nbPageMappings: Dictionary<{ route: Routes, contextLevel: ContextLevel }> = {
    ModernCar_Proposer_CustomerFacing: { route: Routes.scheme, contextLevel: ContextLevel.preQuote },
    ModernCar_Premium_CustomerFacing: { route: Routes.premium, contextLevel: ContextLevel.quote },
    ModernCar_VAPs_CustomerFacing: { route: Routes.additionalProducts, contextLevel: ContextLevel.quote },
    ModernCar_Payment: { route: Routes.payment, contextLevel: ContextLevel.quote },
    ModernCar_PostTakeUpVAPs_CustomerFacing: { route: Routes.upgrade, contextLevel: ContextLevel.policy },
    ModernCar_VAPs_Payment_CustomerFacing: { route: Routes.upgradePayment, contextLevel: ContextLevel.policy },
    ModernCar_PaymentConfirmation: { route: Routes.paymentConfirm, contextLevel: ContextLevel.policy },

    CommercialVehicle_Proposer_CustomerFacing: { route: Routes.scheme, contextLevel: ContextLevel.preQuote },
    CommercialVehicle_Premium_CustomerFacing: { route: Routes.premium, contextLevel: ContextLevel.quote },
    CommercialVehicle_VAPs_CustomerFacing: { route: Routes.additionalProducts, contextLevel: ContextLevel.quote },
    CommercialVehicle_Payment: { route: Routes.payment, contextLevel: ContextLevel.quote },
    CommercialVehicle_PostTakeUpVAPs_CustomerFacing: { route: Routes.upgrade, contextLevel: ContextLevel.policy },
    CommercialVehicle_VAPs_Payment_CustomerFacing: { route: Routes.upgradePayment, contextLevel: ContextLevel.policy },
    CommercialVehicle_PaymentConfirmation: { route: Routes.paymentConfirm, contextLevel: ContextLevel.policy },

    Home_Your_Home_CustomerFacing: { route: Routes.scheme, contextLevel: ContextLevel.preQuote },
    Home_Contents_Occupancy_And_Security_CustomerFacing: { route: Routes.homeContentsOccupancyAndSecurity, contextLevel: ContextLevel.preQuote },
    Home_Proposer_CustomerFacing: { route: Routes.proposerDetails, contextLevel: ContextLevel.preQuote },
    Home_Premium_CustomerFacing: { route: Routes.premium, contextLevel: ContextLevel.quote },
    Home_Payment: { route: Routes.payment, contextLevel: ContextLevel.quote },
    Home_PaymentConfirmation: { route: Routes.paymentConfirm, contextLevel: ContextLevel.policy },
    Home_VAPs_CustomerFacing: { route: Routes.additionalProducts, contextLevel: ContextLevel.quote },
    Home_PostTakeUpVAPs_CustomerFacing: { route: Routes.upgrade, contextLevel: ContextLevel.policy },
    Home_VAPs_Payment_CustomerFacing: { route: Routes.upgradePayment, contextLevel: ContextLevel.policy },
  }

  mtaPageMappings: Dictionary<{ route: Routes, contextLevel: ContextLevel }> = {
    ModernCar_ReasonForChangesMta_CustomerFacing: { route: Routes.mtaReasonsForChanges, contextLevel: ContextLevel.mtaPreQuote },
    ModernCar_ProposerMta_CustomerFacing: { route: Routes.mtaScheme, contextLevel: ContextLevel.mtaPreQuote },
    ModernCar_PremiumMta: { route: Routes.mtaPremium, contextLevel: ContextLevel.mtaQuote },
    ModernCar_Payment: { route: Routes.mtaPayment, contextLevel: ContextLevel.mtaQuote },
    ModernCar_PaymentConfirmationMta: { route: Routes.mtaConfirm, contextLevel: ContextLevel.mtaTakenUp },

    CommercialVehicle_ReasonForChangesMta_CustomerFacing: { route: Routes.mtaReasonsForChanges, contextLevel: ContextLevel.mtaPreQuote },
    CommercialVehicle_ProposerMta_CustomerFacing: { route: Routes.mtaScheme, contextLevel: ContextLevel.mtaPreQuote },
    CommercialVehicle_PremiumMta: { route: Routes.mtaPremium, contextLevel: ContextLevel.mtaQuote },
    CommercialVehicle_Payment: { route: Routes.mtaPayment, contextLevel: ContextLevel.mtaQuote },
    CommercialVehicle_PaymentConfirmationMta: { route: Routes.mtaConfirm, contextLevel: ContextLevel.mtaTakenUp },
    Home_Your_HomeMTA_CustomerFacing: { route: Routes.mtaScheme, contextLevel: ContextLevel.mtaPreQuote },

    Home_Contents_Occupancy_And_SecurityMTA_CustomerFacing: { route: Routes.mtahomeContentsOccupancyAndSecurity, contextLevel: ContextLevel.mtaPreQuote },
    Home_ProposerMTA_CustomerFacing: { route: Routes.mtaProposerDetails, contextLevel: ContextLevel.mtaPreQuote },
    Home_PremiumMTA_CustomerFacing: { route: Routes.mtaPremium, contextLevel: ContextLevel.mtaQuote },
    Home_Payment: { route: Routes.mtaPayment, contextLevel: ContextLevel.mtaQuote },
    Home_PaymentConfirmationMta: { route: Routes.mtaConfirm, contextLevel: ContextLevel.mtaTakenUp },
  }

  renewalPageMappings: Dictionary<{ route: Routes, contextLevel: ContextLevel }> = {
    ModernCar_ProposerRenewal_CustomerFacing: { route: Routes.renewalScheme, contextLevel: ContextLevel.renewalPreQuote },
    ModernCar_PremiumRenewal: { route: Routes.renewalPremium, contextLevel: ContextLevel.renewalQuote },
    ModernCar_PaymentRenewal: { route: Routes.renewalPayment, contextLevel: ContextLevel.renewalQuote },
    ModernCar_PostTakeUpVAPs_CustomerFacing: { route: Routes.renewalUpgrade, contextLevel: ContextLevel.renewalTakenUp },
    ModernCar_VAPs_Payment_CustomerFacing: { route: Routes.renewalUpgradePayment, contextLevel: ContextLevel.renewalTakenUp },
    ModernCar_PaymentConfirmation: { route: Routes.renewalPaymentConfirmation, contextLevel: ContextLevel.renewalTakenUp },

    CommercialVehicle_ProposerRenewal_CustomerFacing: { route: Routes.renewalScheme, contextLevel: ContextLevel.renewalPreQuote },
    CommercialVehicle_PremiumRenewal: { route: Routes.renewalPremium, contextLevel: ContextLevel.renewalQuote },
    CommercialVehicle_PaymentRenewal: { route: Routes.renewalPayment, contextLevel: ContextLevel.renewalQuote },
    CommercialVehicle_PostTakeUpVAPs_CustomerFacing: { route: Routes.upgrade, contextLevel: ContextLevel.renewalTakenUp },
    CommercialVehicle_VAPs_Payment_CustomerFacing: { route: Routes.upgradePayment, contextLevel: ContextLevel.renewalTakenUp },
    CommercialVehicle_PaymentConfirmation: { route: Routes.renewalPaymentConfirmation, contextLevel: ContextLevel.renewalTakenUp },

    Home_Your_Home_Renewal_CustomerFacing: { route: Routes.renewalhomeYourHome, contextLevel: ContextLevel.renewalPreQuote },
    Home_Contents_Occupancy_And_Security_Renewal_CustomerFacing: { route: Routes.renewalhomeContentsOccupancyAndSecurity, contextLevel: ContextLevel.renewalPreQuote },
    Home_Proposer_Renewal_CustomerFacing: { route: Routes.renewalProposerDetails, contextLevel: ContextLevel.renewalPreQuote },
    Home_Premium_Renewal_CustomerFacing: { route: Routes.renewalPremium, contextLevel: ContextLevel.renewalQuote },
    Home_Payment: { route: Routes.renewalPayment, contextLevel: ContextLevel.renewalQuote },
    Home_PaymentConfirmation: { route: Routes.renewalPaymentConfirmation, contextLevel: ContextLevel.renewalTakenUp },
  }

  setupPage(setId: string, productPage: ProductPage, answers: IQuestionAnswer[], configs: IConfiguration, productUniqueId?: ProductEnum): Page {
    const mappedPage = this.mapProductPageToPage(productPage)
    return this.setupDependencies(setId, mappedPage, answers, configs, productUniqueId)
  }

  setupDependencies(setId: string, mappedPage: Page, answers: IQuestionAnswer[], config: IConfiguration, productUniqueId?: ProductEnum): Page {
    switch (setId) {
      case 'ModernCar_ReasonForChangesMta_CustomerFacing':
      case 'CommercialVehicle_ReasonForChangesMta_CustomerFacing':
        return setupReasonsForChangesDependencies(mappedPage)
      case 'ModernCar_ProposerMta_CustomerFacing':
        return setupProposerDependencies(mappedPage, true, config, productUniqueId, answers)
      case 'ModernCar_Proposer_CustomerFacing':
      case 'ModernCar_ProposerRenewal_CustomerFacing':
        return setupProposerDependencies(mappedPage, false, config, productUniqueId)
      case 'CommercialVehicle_ProposerMta_CustomerFacing':
        return setupProposerVanDependencies(mappedPage, true, config, productUniqueId, answers)
      case 'CommercialVehicle_Proposer_CustomerFacing':
      case 'CommercialVehicle_ProposerRenewal_CustomerFacing':
        return setupProposerVanDependencies(mappedPage, false, config, productUniqueId)
      case 'ModernCar_Proposer_CustomerFacing_AdditionalDriver':
      case 'ModernCar_ProposerMta_CustomerFacing_AdditionalDriver':
      case 'ModernCar_ProposerRenewal_CustomerFacing_AdditionalDriver':
      case 'CommercialVehicle_Proposer_CustomerFacing_AdditionalDriver':
      case 'CommercialVehicle_ProposerMta_CustomerFacing_AdditionalDriver':
      case 'CommercialVehicle_ProposerRenewal_CustomerFacing_AdditionalDriver':
        return setupAdditionalDriverDependencies(mappedPage)
      case 'ModernCar_AggregatorLanding':
      case 'ModernCar_AggregatorLanding_AdditionalDriver':
      case 'CommercialVehicle_AggregatorLanding':
      case 'CommercialVehicle_AggregatorLanding_AdditionalDriver':
        return setupAggregatorLandingDependencies(mappedPage)
      case 'Home_Your_Home_CustomerFacing':
      case 'Home_Contents_Occupancy_And_Security_CustomerFacing':
        return setupPropertyDependencies(mappedPage, true)
      case 'Home_Your_HomeMTA_CustomerFacing':
      case 'Home_Your_Home_Renewal_CustomerFacing':
      case 'Home_Contents_Occupancy_And_SecurityMTA_CustomerFacing':
      case 'Home_Contents_Occupancy_And_Security_Renewal_CustomerFacing':
        return setupPropertyDependencies(mappedPage)
      case 'Home_Specified_Items_CustomerFacing':
      case 'Home_Specified_ItemsMTA_CustomerFacing':
      case 'Home_Specified_Items_Renewal_CustomerFacing':
        return setupSpecifiedItems(mappedPage)
      case 'Home_Proposer_CustomerFacing':
        return setupHomeProposerDependencies(mappedPage, true, config, productUniqueId)
      case 'Home_Proposer_Renewal_CustomerFacing':
      case 'Home_ProposerMTA_CustomerFacing':
        return setupHomeProposerDependencies(mappedPage, false, config, productUniqueId)
      case 'Home_Proposer_CustomerFacing_AdditionalProposer':
      case 'Home_ProposerMTA_CustomerFacing_AdditionalProposer':
      case 'Home_Proposer_Renewal_CustomerFacing_AdditionalProposer':
        return setupHomeAdditionalProposerDependencies(mappedPage)
      case 'Home_Claims_CustomerFacing':
        return setupHomeClaimsDependencies(mappedPage)
      case 'Home_Claims_Renewal_CustomerFacing':
        return setupHomeClaimsDependencies(mappedPage, true)
      case 'Home_ClaimsMTA_CustomerFacing':
        return setupHomeMTAClaimsDependencies(mappedPage, config)
      case 'ModernCar_VAPs_CustomerFacing':
      case 'ModernCar_PostTakeUpVAPs_CustomerFacing':
      case 'ModernCar_VAPs_Payment_CustomerFacing':
      case 'ModernCar_Premium_CustomerFacing':
      case 'ModernCar_PremiumMta':
      case 'ModernCar_Payment':
      case 'ModernCar_PaymentConfirmation':
      case 'ModernCar_PaymentConfirmationMta':
      case 'ModernCar_PremiumRenewal':
      case 'ModernCar_PaymentRenewal':
      case 'CommercialVehicle_VAPs_CustomerFacing':
      case 'CommercialVehicle_PostTakeUpVAPs_CustomerFacing':
      case 'CommercialVehicle_VAPs_Payment_CustomerFacing':
      case 'CommercialVehicle_Premium_CustomerFacing':
      case 'CommercialVehicle_PremiumMta':
      case 'CommercialVehicle_Payment':
      case 'CommercialVehicle_PaymentConfirmation':
      case 'CommercialVehicle_PaymentConfirmationMta':
      case 'CommercialVehicle_PremiumRenewal':
      case 'CommercialVehicle_PaymentRenewal':
      case 'Home_Payment':
      case 'Home_Premium_CustomerFacing':
      case 'Home_PremiumMTA_CustomerFacing':
      case 'Home_PaymentConfirmation':
      case 'Home_VAPs_CustomerFacing':
      case 'Home_PostTakeUpVAPs_CustomerFacing':
      case 'Home_VAPs_Payment_CustomerFacing':
      case 'Home_PaymentConfirmationMta':
      case 'Account':
      case 'Home_AggregatorLanding':
      case 'Home_Premium_Renewal_CustomerFacing':
      case 'Home_Payment_Renewal_CustomerFacing':
        return mappedPage
      default:
        throw new UnkownQuestionSetError(setId)
    }
  }

  getSchemeRequest(_questions: IQuestionAnswer[]): SchemeRequest {
    throw new Error('Method not implemented. Yoga does not use Scheme Requests.')
  }

  validate(_questions: IQuestionAnswer[]): boolean {
    throw new Error('Method not implemented. Yoga does not use custom validation.')
  }

  getQuoteRequest<T extends QuoteRequest>(questions: IQuestionAnswer[], Type: new () => T): T {
    const quoteRequest = new Type()

    if ((quoteRequest instanceof CarQuoteRequest || quoteRequest instanceof CarMTAQuoteRequest))
      return getCarQuoteRequest(questions, Type)
    else if (quoteRequest instanceof PropertyQuoteRequest)
      return getPropertyQuoteRequest(questions, Type)

    throw new Error(`Unsupported quote request (${typeof quoteRequest}) for Yoga.`)
  }

  getMTARequest<T extends QuoteRequest>(questions: IQuestionAnswer[], Type: new() => T): T {
    const mtaRequest = this.getQuoteRequest(questions, Type)

    if (!(mtaRequest instanceof CarMTAQuoteRequest) && !(mtaRequest instanceof PropertyMTAQuoteRequest))
      throw new Error(`Unsupported quote request (${typeof mtaRequest}) for Yoga.`)

    if (mtaRequest instanceof CarMTAQuoteRequest) {
      const reasonsForChanges = questions.find(q => q.name === QuestionEnum.reasonForChange) as QuestionAnswer<string[]>
      mtaRequest.reasonForChange = reasonsForChanges.value!.length > 1 ? DLDCommonValuesEnum.ReasonForChange.MultipleChanges : reasonsForChanges.value![0]
    }
    else {
      const reasonForChange = questions.find(q => q.name === QuestionEnum.reasonForChange) as QuestionAnswer<string>
      mtaRequest.reasonForChange = reasonForChange.value!
    }
    return mtaRequest
  }

  mapRiskToAnswers(risk: Risk, account: Account, pages: Page[]): Dictionary<PageAnswers> {
    if (risk instanceof PropertyRisk) {
      const propertyRisk = risk as PropertyRisk
      return mapPropertyRiskToAnswers(propertyRisk, account, pages)
    }

    if (risk instanceof CarRisk) {
      const carRisk = risk as CarRisk
      return mapCarRiskToAnswers(carRisk, account, pages)
    }

    throw new Error('Unsupported risk type for Yoga.')
  }

  getAccountRegisterRequest(questions: IQuestionAnswer[], affiliateId: number | null): AccountRegister | null {
    const title = questions.find(q => q.name === QuestionEnum.title) as QuestionAnswer<string>
    const firstName = questions.find(q => q.name === QuestionEnum.firstname) as QuestionAnswer<string>
    const lastName = questions.find(q => q.name === QuestionEnum.lastName) as QuestionAnswer<string>
    const email = questions.find(q => q.name === QuestionEnum.customerEmail) as QuestionAnswer<string>
    const dob = questions.find(q => q.name === QuestionEnum.dateOfBirth) as QuestionAnswer<Date>
    const address = questions.find(q => q.name === QuestionEnum.address) as QuestionAnswer<Address>
    const phoneNumber = questions.find(q => q.name === QuestionEnum.mobilePhoneNumber) as QuestionAnswer<string>

    if (!title?.value || !firstName?.value || !lastName?.value || !email?.value)
      return null

    const request = new AccountRegister()
    request.title = title.value!
    request.firstName = firstName.value!
    request.lastName = lastName.value!
    request.email = email.value!
    request.dateOfBirth = dob.value!
    request.address = address.value!
    request.mobilePhone = phoneNumber.value!

    request.marketing = new Marketing()
    request.marketing.isEmailOptIn = true
    request.marketing.isTelephoneOptIn = true
    request.marketing.isSMSOptIn = true
    request.marketing.isPostOptIn = true

    request.affiliateId = affiliateId

    return request
  }

  mapMarketingToQuestion(account: Account, marketing: SelectQuestion<string[], string>): void {
    marketing.value = []
    if (!account.marketing.isTelephoneOptIn)
      marketing.value.push(DLDCommonValuesEnum.MarketingOption.Telephone)
    if (!account.marketing.isEmailOptIn)
      marketing.value.push(DLDCommonValuesEnum.MarketingOption.Email)
    if (!account.marketing.isPostOptIn)
      marketing.value.push(DLDCommonValuesEnum.MarketingOption.Post)
    if (!account.marketing.isSMSOptIn)
      marketing.value.push(DLDCommonValuesEnum.MarketingOption.SMS)
  }

  updateAccountFromQuestions(account: Account, questions: IQuestionAnswer[]): Account {
    const title = questions.find(q => q.name === QuestionEnum.title) as QuestionAnswer<string>
    const firstName = questions.find(q => q.name === QuestionEnum.firstname) as QuestionAnswer<string>
    const lastName = questions.find(q => q.name === QuestionEnum.lastName) as QuestionAnswer<string>
    const email = questions.find(q => q.name === QuestionEnum.customerEmail) as QuestionAnswer<string>
    const phoneNumber = questions.find(q => q.name === QuestionEnum.mobilePhoneNumber || q.name === QuestionEnum.customerHomePhoneNumber) as QuestionAnswer<string>
    const dateOfBirth = questions.find(q => q.name === QuestionEnum.dateOfBirth) as QuestionAnswer<Date>
    const marketing = questions.find(q => q.name === QuestionEnum.keepingYouInformed) as QuestionAnswer<string[]>
    const address = questions.find(q => q.name === QuestionEnum.address) as QuestionAnswer<Address>
    const autoRenew = questions.find(q => q.name === QuestionEnum.autoRenewalOptOut) as QuestionAnswer<string>

    account.title = title?.value ?? account.title
    account.firstName = firstName?.value ?? account.firstName
    account.lastName = lastName?.value ?? account.lastName
    account.email = email?.value ?? account.email
    account.homePhone = phoneNumber?.value ?? account.homePhone
    account.mobilePhone = phoneNumber?.value ?? account.mobilePhone
    account.telephone = phoneNumber?.value ?? account.telephone
    account.workPhone = phoneNumber?.value ?? account.workPhone
    account.dateOfBirth = dateOfBirth?.value ?? account.dateOfBirth
    if (marketing?.value) {
      account.marketing.isEmailOptIn = marketing.value?.indexOf(DLDCommonValuesEnum.MarketingOption.Email) === -1
      account.marketing.isSMSOptIn = marketing.value?.indexOf(DLDCommonValuesEnum.MarketingOption.SMS) === -1
      account.marketing.isPostOptIn = marketing.value?.indexOf(DLDCommonValuesEnum.MarketingOption.Post) === -1
      account.marketing.isTelephoneOptIn = marketing.value?.indexOf(DLDCommonValuesEnum.MarketingOption.Telephone) === -1
    }
    account.address = address?.value ?? account.address
    if (autoRenew?.value)
      account.autoRenew = autoRenew.value?.indexOf('true') === -1

    return account
  }
}
